import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { db } from '../config/firebase';
import { collection, query, where, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';

// --- ICONS FÜR DAS UI ---
const IconBase = ({ children, size = 24, className = "" }: any) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>{children}</svg>;
const CheckCircleIcon = (props: any) => <IconBase {...props}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></IconBase>;
const AlertTriangleIcon = (props: any) => <IconBase {...props}><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></IconBase>;
const MailXIcon = (props: any) => <IconBase {...props}><path d="M22 13V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h9"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/><path d="m17 17 4 4"/><path d="m21 17-4 4"/></IconBase>;
const LoaderIcon = (props: any) => <IconBase {...props}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></IconBase>;

const UnsubscribePage: React.FC = () => {
    const [searchParams] = useSearchParams();
    // 💉 NEUER STATE: 'confirm' als Warteschleife, 'cancelled' für Abbruch
    const [status, setStatus] = useState<'loading' | 'confirm' | 'success' | 'not_found' | 'error' | 'cancelled'>('loading');
    const [processedEmail, setProcessedEmail] = useState<string | null>(null);

    // 1. SCHRITT: URL Parsen, E-Mail sichern, aber NOCH NICHTS löschen
    useEffect(() => {
        const emailParam = searchParams.get('email');
        
        if (!emailParam) {
            setStatus('error');
            return;
        }

        setProcessedEmail(emailParam.toLowerCase().trim());
        setStatus('confirm'); // Warten auf User-Klick
    }, [searchParams]);

    // 2. SCHRITT: Die scharfe Lösch-Funktion (wird nur per Button ausgelöst)
    const executeUnsubscribe = async () => {
        if (!processedEmail) return;
        
        setStatus('loading');

        try {
            let foundInCrm = false;
            let updatedGlobal = false;

            // Suche im CRM (Kalkulation Mandanten)
            const crmQuery = query(collection(db, 'kalkulation_mandanten'), where('email', '==', processedEmail));
            const crmSnapshot = await getDocs(crmQuery);

            if (!crmSnapshot.empty) {
                foundInCrm = true;
                const updatePromises = crmSnapshot.docs.map(docSnap => {
                    return updateDoc(doc(db, 'kalkulation_mandanten', docSnap.id), {
                        newsletter_opt_out: true,
                        'clientData.newsletter_opt_out': true
                    });
                });
                await Promise.all(updatePromises);
            }

            // Suche in den globalen Adressbüchern (Gruppe B & C)
            const globalRef = doc(db, 'newsletter_system', 'global_contacts');
            const globalSnap = await getDoc(globalRef);

            if (globalSnap.exists()) {
                const data = globalSnap.data();
                const pastedMandanten = data.pastedMandanten || [];
                const externalLeads = data.externalLeads || [];

                const newPasted = pastedMandanten.filter((m: any) => m.email.toLowerCase() !== processedEmail);
                const newExternal = externalLeads.filter((e: string) => e.toLowerCase() !== processedEmail);

                if (newPasted.length !== pastedMandanten.length || newExternal.length !== externalLeads.length) {
                    await updateDoc(globalRef, {
                        pastedMandanten: newPasted,
                        externalLeads: newExternal
                    });
                    updatedGlobal = true;
                }
            }

            if (foundInCrm || updatedGlobal) {
                setStatus('success');
            } else {
                setStatus('not_found');
            }

        } catch (error) {
            console.error("Fehler beim Abmelden:", error);
            setStatus('error');
        }
    };

    return (
        <div className="min-h-screen bg-[#08090b] flex items-center justify-center p-4 font-sans">
            <div className="bg-[#15171e] border border-slate-800 rounded-2xl p-8 max-w-md w-full shadow-2xl animate-fade-in relative overflow-hidden">
                
                {/* Deko-Element */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-[#E6C299] to-emerald-500 opacity-50"></div>

                <div className="text-center flex flex-col items-center">
                    
                    {/* 💉 NEU: SICHERHEITS-ABFRAGE */}
                    {status === 'confirm' && (
                        <>
                            <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mb-6 border border-slate-700">
                                <MailXIcon size={32} className="text-[#E6C299]" />
                            </div>
                            <h1 className="text-2xl font-black text-white uppercase tracking-widest mb-2">Newsletter Abmelden</h1>
                            <p className="text-slate-400 text-sm mb-8 leading-relaxed">
                                Möchtest du die E-Mail Adresse <br/>
                                <strong className="text-white text-base">{processedEmail}</strong><br/>
                                wirklich endgültig aus unserem Verteiler entfernen?
                            </p>
                            
                            <div className="flex flex-col gap-3 w-full">
                                <button
                                    onClick={executeUnsubscribe}
                                    className="w-full bg-red-600 hover:bg-red-500 text-white font-black uppercase tracking-widest text-sm py-4 rounded-xl transition-all shadow-lg shadow-red-900/20 border border-red-500/50 hover:border-red-400 flex justify-center items-center gap-2"
                                >
                                    Ja, endgültig abmelden
                                </button>
                                <button
                                    onClick={() => setStatus('cancelled')}
                                    className="w-full bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold uppercase tracking-widest text-xs py-3 rounded-xl transition-all border border-slate-700"
                                >
                                    Abbrechen
                                </button>
                            </div>
                        </>
                    )}

                    {/* 💉 NEU: ABBRUCH-SCREEN */}
                    {status === 'cancelled' && (
                        <>
                            <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mb-6 border border-slate-700">
                                <CheckCircleIcon size={32} className="text-slate-400" />
                            </div>
                            <h1 className="text-2xl font-black text-white uppercase tracking-widest mb-2">Abbruch</h1>
                            <p className="text-slate-400 text-sm mb-4">
                                Der Vorgang wurde abgebrochen. Die Adresse <strong className="text-white">{processedEmail}</strong> bleibt weiterhin in unserem System.
                            </p>
                            <div className="text-[10px] text-slate-500 font-mono bg-slate-900/50 px-3 py-2 rounded-lg border border-slate-800">
                                Du kannst dieses Fenster jetzt sicher schließen.
                            </div>
                        </>
                    )}

                    {status === 'loading' && (
                        <>
                            <div className="w-20 h-20 bg-blue-900/20 rounded-full flex items-center justify-center mb-6 border border-blue-500/30">
                                <LoaderIcon size={32} className="text-blue-500 animate-spin" />
                            </div>
                            <h1 className="text-2xl font-black text-white uppercase tracking-widest mb-2">Verarbeite...</h1>
                            <p className="text-slate-400 text-sm">Bitte warte einen Moment, das System wird aktualisiert.</p>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <div className="w-20 h-20 bg-emerald-900/20 rounded-full flex items-center justify-center mb-6 border border-emerald-500/30 shadow-[0_0_30px_rgba(16,185,129,0.2)] animate-scale-in">
                                <CheckCircleIcon size={40} className="text-emerald-500" />
                            </div>
                            <h1 className="text-2xl font-black text-white uppercase tracking-widest mb-2">Erfolgreich abgemeldet</h1>
                            <p className="text-slate-400 text-sm mb-4">
                                Die E-Mail Adresse <strong className="text-white">{processedEmail}</strong> wurde erfolgreich aus unseren Systemen entfernt.
                            </p>
                            <div className="text-[10px] text-emerald-400/70 font-mono bg-emerald-900/10 px-3 py-2 rounded-lg border border-emerald-800/30">
                                Du erhältst ab sofort keine weiteren Newsletter.
                            </div>
                        </>
                    )}

                    {status === 'not_found' && (
                        <>
                            <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mb-6 border border-slate-700">
                                <MailXIcon size={32} className="text-slate-400" />
                            </div>
                            <h1 className="text-2xl font-black text-white uppercase tracking-widest mb-2">Nicht gefunden</h1>
                            <p className="text-slate-400 text-sm mb-4">
                                Die E-Mail Adresse <strong className="text-white">{processedEmail}</strong> befindet sich auf keiner unserer Newsletter-Listen.
                            </p>
                            <div className="text-[10px] text-slate-500 font-mono bg-slate-900/50 px-3 py-2 rounded-lg border border-slate-800">
                                Es ist keine weitere Aktion erforderlich.
                            </div>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <div className="w-20 h-20 bg-red-900/20 rounded-full flex items-center justify-center mb-6 border border-red-500/30">
                                <AlertTriangleIcon size={32} className="text-red-500" />
                            </div>
                            <h1 className="text-2xl font-black text-white uppercase tracking-widest mb-2">Fehler aufgetreten</h1>
                            <p className="text-slate-400 text-sm mb-4">
                                Der Abmelde-Link ist ungültig oder abgelaufen. Bitte prüfe, ob die E-Mail Adresse korrekt in der Adresszeile steht.
                            </p>
                            <div className="text-[10px] text-red-400/70 font-mono bg-red-900/10 px-3 py-2 rounded-lg border border-red-800/30">
                                Format: ?email=deine@mail.de
                            </div>
                        </>
                    )}

                </div>
            </div>
        </div>
    );
};

export default UnsubscribePage;